import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Chip,
  Fab,
  IconButton,
} from "@mui/material";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import Chart from "react-apexcharts";
import { get_marketing_funnel_overview_api, post_marketing_assistant_chat_api } from "../../DAL/Marketing/Marketing";

const THEME_COLOR = "#c01e2e";

const TIME_PERIODS = [
  { id: 7, label: "7D" },
  { id: 30, label: "30D" },
  { id: 90, label: "90D" },
  { id: 3650, label: "All" },
];

const REVENUE_TYPE_LABELS = {
  subscription: "Subscription",
  token_purchase: "Credits Top-up",
  explanation_credits: "Credits Top-up",
  exam_insurance: "Exam Insurance",
  ground_instructor_credits: "Ground Instructor Credits",
  pass_guarantee: "Pass Guarantee",
  flight_school_seat: "Flight School Seat",
  flight_school_ai_usage: "Flight School AI Usage",
  other: "Other",
};

const cardSx = {
  p: 2.5,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(26,26,26,0.09)",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
};

const cellSx = { fontFamily: "'Rubik', sans-serif", fontSize: "12px", color: "#1a1a1a", borderColor: "rgba(26,26,26,0.07)" };
const headCellSx = { ...cellSx, color: "#aaa", fontWeight: 600, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.3px" };

const formatGBP = (amount) =>
  `£${(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const formatPct = (val) => `${(val || 0).toFixed(0)}%`;
const formatRevenueType = (type) => REVENUE_TYPE_LABELS[type] || type;

const StatCard = ({ icon, value, label, color, sublabel, comparison }) => (
  <Paper sx={{ ...cardSx, p: 2, display: "flex", alignItems: "center", gap: 2 }}>
    <Box sx={{ width: 44, height: 44, borderRadius: "11px", backgroundColor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: "#1a1a1a", fontSize: "26px", fontWeight: 700, lineHeight: 1, fontFamily: "'Rubik', sans-serif" }}>{value}</Typography>
      <Typography sx={{ color: "#aaa", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.3px", fontFamily: "'Rubik', sans-serif" }}>{label}</Typography>
      {sublabel && <Typography sx={{ color, fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>{sublabel}</Typography>}
      {comparison}
    </Box>
  </Paper>
);

// Shows the % change of `current` vs `previous` (the immediately preceding
// period of equal length), e.g. "+12% vs prior period".
const ComparisonBadge = ({ current, previous }) => {
  if (!previous && !current) return null;
  let text;
  let positive;
  if (!previous) {
    text = "New this period";
    positive = true;
  } else {
    const pct = ((current - previous) / previous) * 100;
    positive = pct >= 0;
    text = `${positive ? "+" : ""}${pct.toFixed(0)}% vs prior period`;
  }
  return (
    <Typography sx={{ color: positive ? "#22c55e" : "#c01e2e", fontSize: "11px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
      {text}
    </Typography>
  );
};

// Small centered "big number + label" block used inside pathway cards
// (Subscription Plan Path / Pay-as-you-go Credits Path) to surface the
// counts the admin cares about most at a glance.
const MiniStat = ({ value, label, sublabel, color }) => (
  <Box sx={{ textAlign: "center" }}>
    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: color || "#1a1a1a", fontFamily: "'Rubik', sans-serif", lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "12px", color: "#1a1a1a", mt: 0.5, fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>{label}</Typography>
    {sublabel && <Typography sx={{ fontSize: "11px", color: "#aaa", fontFamily: "'Rubik', sans-serif" }}>{sublabel}</Typography>}
  </Box>
);

const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
    <Box sx={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: "rgba(192,30,46,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>{title}</Typography>
      {subtitle && <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

const EmptyState = ({ text }) => (
  <Typography sx={{ color: "#aaa", fontSize: "13px", textAlign: "center", py: 4, fontFamily: "'Rubik', sans-serif" }}>{text}</Typography>
);

// Horizontal bar chart used for funnels (stages in decreasing order) and
// simple comparisons (e.g. Paid vs Not Paid). `percentOf` controls whether
// the % label is relative to the largest stage ("max", for funnel drop-off)
// or to the sum of all stages ("sum", for share-of-total comparisons).
const HorizontalBarChart = ({ stages, colors, percentOf = "max", compact = false }) => {
  const total = stages.reduce((sum, s) => sum + (s.value || 0), 0);
  const max = Math.max(...stages.map((s) => s.value || 0), 1);
  const denom = percentOf === "sum" ? total : max;
  const palette = colors || [THEME_COLOR];

  const options = {
    chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
    plotOptions: { bar: { horizontal: true, barHeight: compact ? "65%" : "50%", borderRadius: 4, distributed: palette.length > 1 } },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toLocaleString()} (${denom > 0 ? ((val / denom) * 100).toFixed(0) : 0}%)`,
      style: { colors: ["#1a1a1a"], fontSize: "12px", fontFamily: "'Rubik', sans-serif" },
      offsetX: 8,
    },
    xaxis: {
      categories: stages.map((s) => s.name),
      labels: { style: { colors: "#6b6b6b", fontSize: "11px" }, formatter: (v) => Math.round(v) },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: "#1a1a1a", fontSize: "12px", fontFamily: "'Rubik', sans-serif" } } },
    grid: { borderColor: "rgba(26,26,26,0.07)", strokeDashArray: 4, xaxis: { lines: { show: true } } },
    colors: palette,
    legend: { show: false },
    tooltip: { theme: "light" },
  };

  return <Chart options={options} series={[{ name: "Users", data: stages.map((s) => s.value || 0) }]} type="bar" height={Math.max(stages.length * (compact ? 42 : 70), 140)} />;
};

const DropoffRow = ({ stages, dropoffs }) => {
  if (!dropoffs || dropoffs.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 2, pt: 2, borderTop: "1px solid rgba(26,26,26,0.07)" }}>
      {dropoffs.map((pct, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>
            {stages[i].name} → {stages[i + 1].name}
          </Typography>
          <Typography sx={{ color: pct > 50 ? "#c01e2e" : "#f59e0b", fontSize: "12px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
            -{pct.toFixed(0)}%
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const ASSISTANT_GREETING =
  "Hi! I'm your marketing assistant. Ask me about signups, funnels, revenue, campaigns, or specific users and I'll dig into the live data for you.";

// Floating chat widget giving admins a Claude-powered assistant with read
// access to acquisition, funnel, revenue, and user data. `context` carries
// the dashboard's current filters (date range + channel) so the assistant
// can answer "for this view" style questions.
const MarketingAssistantChat = ({ context }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isSending, open]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setIsSending(true);

    try {
      const response = await post_marketing_assistant_chat_api({
        message: trimmed,
        conversationHistory: history,
        context,
      });
      if (response && response.success !== false && response.data) {
        setMessages((prev) => [...prev, { role: "assistant", content: response.data.message }]);
      } else {
        setMessages((prev) => [...prev, { role: "assistant", content: response?.error || "Sorry, something went wrong. Please try again." }]);
      }
    } catch (error) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) {
    return (
      <Fab
        onClick={() => setOpen(true)}
        sx={{ position: "fixed", bottom: 24, right: 24, backgroundColor: THEME_COLOR, color: "#fff", "&:hover": { backgroundColor: "#a01825" }, zIndex: 1300 }}
      >
        <SmartToyOutlinedIcon />
      </Fab>
    );
  }

  return (
    <Paper
      sx={{
        ...cardSx,
        position: "fixed",
        bottom: 24,
        right: 24,
        width: 380,
        height: 520,
        display: "flex",
        flexDirection: "column",
        p: 0,
        overflow: "hidden",
        zIndex: 1300,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, backgroundColor: THEME_COLOR, color: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>Marketing Assistant</Typography>
        </Box>
        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1.5, backgroundColor: "#FAF7F2" }}>
        {messages.length === 0 && (
          <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif", textAlign: "center", mt: 2 }}>
            {ASSISTANT_GREETING}
          </Typography>
        )}
        {messages.map((m, i) => (
          <Box key={i} sx={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
            <Box
              sx={{
                backgroundColor: m.role === "user" ? THEME_COLOR : "#fff",
                color: m.role === "user" ? "#fff" : "#1a1a1a",
                borderRadius: "10px",
                px: 1.5,
                py: 1,
                border: m.role === "user" ? "none" : "1px solid rgba(26,26,26,0.08)",
              }}
            >
              <Typography sx={{ fontSize: "13px", fontFamily: "'Rubik', sans-serif", whiteSpace: "pre-wrap" }}>{m.content}</Typography>
            </Box>
          </Box>
        ))}
        {isSending && (
          <Box sx={{ alignSelf: "flex-start", pl: 0.5 }}>
            <CircularProgress size={16} sx={{ color: THEME_COLOR }} />
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1, p: 1.5, borderTop: "1px solid rgba(26,26,26,0.08)" }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={3}
          placeholder="Ask about signups, revenue, users…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSending}
          sx={{ "& .MuiInputBase-input": { fontSize: "13px", fontFamily: "'Rubik', sans-serif" } }}
        />
        <IconButton onClick={handleSend} disabled={isSending || !input.trim()} sx={{ color: THEME_COLOR }}>
          <SendOutlinedIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Marketing() {
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState("");
  const [channelOptions, setChannelOptions] = useState([]);
  const [customRange, setCustomRange] = useState({ startDate: "", endDate: "" });

  const isCustomRange = !!(customRange.startDate && customRange.endDate);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, customRange.startDate, customRange.endDate, channel]);

  useEffect(() => {
    fetchChannelOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await get_marketing_funnel_overview_api(
        isCustomRange
          ? { startDate: customRange.startDate, endDate: customRange.endDate, channel }
          : { days, channel }
      );
      if (response && response.success !== false && response.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch marketing funnel data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Populated once from the full history so the channel dropdown keeps every
  // known channel even when the current view is filtered down to one of them.
  const fetchChannelOptions = async () => {
    try {
      const response = await get_marketing_funnel_overview_api({ days: 3650 });
      if (response && response.success !== false && response.data) {
        setChannelOptions((response.data.channels || []).map((c) => c.channel).sort());
      }
    } catch (error) {
      console.error("Failed to fetch channel options:", error);
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  const channels = data?.channels || [];
  const adFunnel = data?.adFunnel || { stages: [], dropoffs: [] };
  const onboardingFunnel = data?.onboardingFunnel || { stages: [], dropoffs: [] };
  const conversionFunnel = data?.conversionFunnel || {
    totalSignups: 0,
    onboarded: 0,
    pathways: { plan: { count: 0, paid: 0 }, credits: { count: 0, depleted: 0, paid: 0 } },
    overallPaid: { paid: 0, notPaid: 0 },
    creditEngagement: { onboarded: 0, usedCredits: 0, usedCreditsRate: 0, ranOut: 0, ranOutRate: 0, ranOutAndPaid: 0, ranOutAndPaidRate: 0 },
  };
  const paymentBreakdown = data?.paymentBreakdown || { buckets: [], byType: [] };
  const creditUsageBeforePurchase = data?.creditUsageBeforePurchase || {
    count: 0,
    avgCreditsUsed: null,
    medianCreditsUsed: null,
    avgBalanceBeforePurchase: null,
  };
  const comparison = data?.comparison || { current: { signups: 0, onboarded: 0, paid: 0, revenue: 0 }, previous: { signups: 0, onboarded: 0, paid: 0, revenue: 0 } };
  const stripeRevenue = data?.stripeRevenue || { amount: null, count: 0, truncated: false };
  const periodActivity = data?.periodActivity || { totalRevenue: 0, paymentCount: 0, payingUsers: 0, newPayers: 0, returningPayers: 0, byType: [] };
  const showComparison = isCustomRange || days !== 3650;

  const totalRevenue = channels.reduce((sum, c) => sum + (c.totalRevenue || 0), 0);
  const onboardingRate = conversionFunnel.totalSignups > 0
    ? (conversionFunnel.onboarded / conversionFunnel.totalSignups) * 100 : 0;
  // Paid is no longer gated on onboarding completion (a user can pay without
  // ever ticking that box), so this is out of totalSignups, not onboarded.
  const paidRate = conversionFunnel.totalSignups > 0
    ? (conversionFunnel.overallPaid.paid / conversionFunnel.totalSignups) * 100 : 0;
  const prevOnboardingRate = comparison.previous.signups > 0
    ? (comparison.previous.onboarded / comparison.previous.signups) * 100 : 0;
  const prevPaidRate = comparison.previous.signups > 0
    ? (comparison.previous.paid / comparison.previous.signups) * 100 : 0;

  const { plan, credits } = conversionFunnel.pathways;
  const planPaidRate = plan.count > 0 ? (plan.paid / plan.count) * 100 : 0;
  const creditEngagement = conversionFunnel.creditEngagement || { onboarded: 0, usedCredits: 0, usedCreditsRate: 0, ranOut: 0, ranOutRate: 0, ranOutAndPaid: 0, ranOutAndPaidRate: 0 };

  const creditsFunnelStages = [
    { name: "Credits Path", value: credits.count },
    { name: "Depleted Credits", value: credits.depleted },
    { name: "Purchased Top-up", value: credits.paid },
  ];
  const creditsFunnelDropoffs = [];
  for (let i = 0; i < creditsFunnelStages.length - 1; i++) {
    const from = creditsFunnelStages[i].value;
    const to = creditsFunnelStages[i + 1].value;
    creditsFunnelDropoffs.push(from > 0 ? ((from - to) / from) * 100 : 0);
  }
  const creditsDepletedRate = credits.count > 0 ? (credits.depleted / credits.count) * 100 : 0;
  const creditsPaidRate = credits.count > 0 ? (credits.paid / credits.count) * 100 : 0;

  const channelChartStages = channels.map((c) => ({ name: c.channel, value: c.signups }));

  const donutOptions = {
    chart: { type: "donut", background: "transparent" },
    labels: ["Subscription Plan", "Pay-as-you-go Credits"],
    colors: [THEME_COLOR, "#45B7D1"],
    legend: { position: "bottom", fontFamily: "'Rubik', sans-serif", labels: { colors: "#6b6b6b" } },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%` },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: "Onboarded",
              color: "#1a1a1a",
              fontFamily: "'Rubik', sans-serif",
            },
          },
        },
      },
    },
    tooltip: { theme: "light" },
  };

  const paymentOutcomeStages = [
    { name: "No Payment", value: conversionFunnel.overallPaid.notPaid },
    { name: "Paid", value: conversionFunnel.overallPaid.paid },
  ];

  const bucketChartOptions = {
    chart: { type: "bar", toolbar: { show: false }, background: "transparent" },
    plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
    dataLabels: { enabled: true, style: { colors: ["#1a1a1a"], fontSize: "11px", fontFamily: "'Rubik', sans-serif" }, offsetY: -18 },
    xaxis: {
      categories: paymentBreakdown.buckets.map((b) => b.range),
      labels: { style: { colors: "#6b6b6b", fontSize: "10px" }, rotate: -30 },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: "#6b6b6b", fontSize: "10px" }, formatter: (v) => Math.round(v) } },
    grid: { borderColor: "rgba(26,26,26,0.07)", strokeDashArray: 4 },
    colors: [THEME_COLOR],
    legend: { show: false },
    tooltip: { theme: "light" },
  };

  const totalBucketCount = paymentBreakdown.buckets.reduce((sum, b) => sum + b.count, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: "#1a1a1a", fontWeight: 600, mb: 0.5, fontFamily: "'Rubik', sans-serif" }}>Marketing</Typography>
          <Typography sx={{ color: "#aaa", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>Acquisition sources, funnels & conversion</Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              displayEmpty
              sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px", backgroundColor: "#fff" }}
            >
              <MenuItem value="" sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px" }}>All Channels</MenuItem>
              {channelOptions.map((c) => (
                <MenuItem key={c} value={c} sx={{ fontFamily: "'Rubik', sans-serif", fontSize: "13px" }}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              type="date"
              size="small"
              label="From"
              value={customRange.startDate}
              onChange={(e) => setCustomRange((r) => ({ ...r, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: customRange.endDate || todayStr() }}
              sx={{ width: 145, backgroundColor: "#fff", "& .MuiInputBase-input": { fontFamily: "'Rubik', sans-serif", fontSize: "13px" } }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={customRange.endDate}
              onChange={(e) => setCustomRange((r) => ({ ...r, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: customRange.startDate || undefined, max: todayStr() }}
              sx={{ width: 145, backgroundColor: "#fff", "& .MuiInputBase-input": { fontFamily: "'Rubik', sans-serif", fontSize: "13px" } }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: "2px", backgroundColor: "rgba(26,26,26,0.06)", borderRadius: "6px", p: "2px" }}>
            {TIME_PERIODS.map((period) => (
              <Box
                key={period.id}
                onClick={() => { setDays(period.id); setCustomRange({ startDate: "", endDate: "" }); }}
                sx={{
                  px: 1.5, py: 0.5, fontSize: "11px", fontWeight: 500, fontFamily: "'Rubik', sans-serif",
                  color: !isCustomRange && days === period.id ? "#fff" : "#6b6b6b",
                  backgroundColor: !isCustomRange && days === period.id ? THEME_COLOR : "transparent",
                  borderRadius: "4px", cursor: "pointer",
                }}
              >
                {period.label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Active filters */}
      {(channel || isCustomRange) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "12px", color: "#aaa", fontFamily: "'Rubik', sans-serif" }}>Showing:</Typography>
          {channel && (
            <Chip
              label={`Channel: ${channel}`}
              onDelete={() => setChannel("")}
              size="small"
              sx={{ backgroundColor: "rgba(192,30,46,0.08)", color: THEME_COLOR, fontFamily: "'Rubik', sans-serif", fontSize: "12px", fontWeight: 500 }}
            />
          )}
          {isCustomRange && (
            <Chip
              label={`${customRange.startDate} → ${customRange.endDate}`}
              onDelete={() => setCustomRange({ startDate: "", endDate: "" })}
              size="small"
              sx={{ backgroundColor: "rgba(192,30,46,0.08)", color: THEME_COLOR, fontFamily: "'Rubik', sans-serif", fontSize: "12px", fontWeight: 500 }}
            />
          )}
        </Box>
      )}

      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2, mb: 3 }}>
        <StatCard
          icon={<GroupAddOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 24 }} />}
          value={conversionFunnel.totalSignups.toLocaleString()}
          label="Signups"
          color={THEME_COLOR}
          comparison={showComparison && <ComparisonBadge current={comparison.current.signups} previous={comparison.previous.signups} />}
        />
        <StatCard
          icon={<CheckCircleOutlineIcon sx={{ color: "#22c55e", fontSize: 24 }} />}
          value={formatPct(onboardingRate)}
          label="Onboarding Rate"
          color="#22c55e"
          sublabel={`${conversionFunnel.onboarded} completed`}
          comparison={showComparison && <ComparisonBadge current={onboardingRate} previous={prevOnboardingRate} />}
        />
        <StatCard
          icon={<PaidOutlinedIcon sx={{ color: "#f59e0b", fontSize: 24 }} />}
          value={formatPct(paidRate)}
          label="Paid Conversion"
          color="#f59e0b"
          sublabel={`${conversionFunnel.overallPaid.paid} paying`}
          comparison={showComparison && <ComparisonBadge current={paidRate} previous={prevPaidRate} />}
        />
        <StatCard
          icon={<TrendingUpOutlinedIcon sx={{ color: "#45B7D1", fontSize: 24 }} />}
          value={formatGBP(totalRevenue)}
          label="Revenue (Period)"
          color="#45B7D1"
          sublabel="Lifetime revenue of users signed up in this period"
          comparison={showComparison && <ComparisonBadge current={comparison.current.revenue} previous={comparison.previous.revenue} />}
        />
        <StatCard
          icon={<AccountBalanceOutlinedIcon sx={{ color: "#8b5cf6", fontSize: 24 }} />}
          value={stripeRevenue.amount != null ? formatGBP(stripeRevenue.amount) : "—"}
          label="Stripe Revenue (Web Only)"
          color="#8b5cf6"
          sublabel={stripeRevenue.amount != null ? `${stripeRevenue.count} charges${stripeRevenue.truncated ? " (truncated)" : ""}${channel ? " · platform-wide" : ""} · excludes Apple/IAP` : "Unavailable"}
        />
      </Box>

      {/* Payment Activity — scoped by when the payment happened, not when the
          payer signed up. This is what moves the moment a payment comes in,
          regardless of whether that payer's signup falls in the window above. */}
      <Paper sx={{ ...cardSx, mb: 3 }}>
        <SectionHeader
          icon={<BoltOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
          title="Payment Activity (This Period)"
          subtitle="What actually happened, payment-wise, in this window — by payment date, not signup date"
        />
        {periodActivity.paymentCount > 0 ? (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2, py: 2 }}>
              <MiniStat value={periodActivity.newPayers} label="New Paying Users" sublabel="first-ever payment in this window" color="#22c55e" />
              <MiniStat value={periodActivity.returningPayers} label="Returning Payers" sublabel="renewals / top-ups" color="#45B7D1" />
              <MiniStat value={periodActivity.paymentCount} label="Payments Collected" color={THEME_COLOR} />
              <MiniStat value={formatGBP(periodActivity.totalRevenue)} label="Revenue Collected" color="#8b5cf6" />
            </Box>
            {periodActivity.byType.length > 0 && (
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Payment Type</TableCell>
                    <TableCell sx={headCellSx} align="right">Payments</TableCell>
                    <TableCell sx={headCellSx} align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodActivity.byType.map((t) => (
                    <TableRow key={t.type}>
                      <TableCell sx={cellSx}>{formatRevenueType(t.type)}</TableCell>
                      <TableCell sx={cellSx} align="right">{t.count}</TableCell>
                      <TableCell sx={cellSx} align="right">{formatGBP(t.totalRevenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        ) : (
          <EmptyState text="No payments collected in this period" />
        )}
      </Paper>

      {/* Acquisition Channels */}
      <Paper sx={{ ...cardSx, mb: 3 }}>
        <SectionHeader
          icon={<PublicOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
          title="Acquisition Channels"
          subtitle="Where signups in this period came from"
        />
        {channels.length > 0 ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" }, gap: 3 }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Channel</TableCell>
                    <TableCell sx={headCellSx} align="right">Signups</TableCell>
                    <TableCell sx={headCellSx} align="right">Onboarded</TableCell>
                    <TableCell sx={headCellSx} align="right">Plan / Credits</TableCell>
                    <TableCell sx={headCellSx} align="right">Paid</TableCell>
                    <TableCell sx={headCellSx} align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {channels.map((c) => (
                    <TableRow key={c.channel}>
                      <TableCell sx={cellSx}>{c.channel}</TableCell>
                      <TableCell sx={cellSx} align="right">{c.signups}</TableCell>
                      <TableCell sx={cellSx} align="right">{formatPct(c.onboardingRate)}</TableCell>
                      <TableCell sx={cellSx} align="right">{c.planPath} / {c.creditsPath}</TableCell>
                      <TableCell sx={cellSx} align="right">{formatPct(c.paidRate)}</TableCell>
                      <TableCell sx={cellSx} align="right">{formatGBP(c.totalRevenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <HorizontalBarChart stages={channelChartStages} percentOf="sum" />
          </Box>
        ) : (
          <EmptyState text="No signups in this period" />
        )}
      </Paper>

      {/* Ad Traffic Funnel */}
      <Paper sx={{ ...cardSx, mb: 3 }}>
        <SectionHeader
          icon={<FilterAltOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
          title="Ad Traffic Funnel"
          subtitle="Tagged ad/campaign traffic only — site visit → register page → signup → onboarded"
        />
        {adFunnel.stages.some((s) => s.value > 0) ? (
          <>
            <HorizontalBarChart stages={adFunnel.stages} />
            <DropoffRow stages={adFunnel.stages} dropoffs={adFunnel.dropoffs} />
          </>
        ) : (
          <EmptyState text="No tagged ad/campaign traffic in this period" />
        )}
      </Paper>

      {/* Onboarding Funnel — all 11 steps of the onboarding wizard */}
      <Paper sx={{ ...cardSx, mb: 3 }}>
        <SectionHeader
          icon={<AccountTreeOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
          title="Onboarding Funnel"
          subtitle="All users, regardless of acquisition source — where people get to in the onboarding wizard"
        />
        {onboardingFunnel.stages.length > 0 && onboardingFunnel.stages[0].value > 0 ? (
          <>
            <HorizontalBarChart stages={onboardingFunnel.stages} compact />
            <DropoffRow stages={onboardingFunnel.stages} dropoffs={onboardingFunnel.dropoffs} />
          </>
        ) : (
          <EmptyState text="No signups in this period" />
        )}
      </Paper>

      {/* Pathway Split + Plan Path */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, mb: 3 }}>
        <Paper sx={cardSx}>
          <SectionHeader
            icon={<DonutLargeOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
            title="Pathway Split"
            subtitle="Plan vs Pay-as-you-go (of onboarded users)"
          />
          {conversionFunnel.onboarded > 0 ? (
            <Chart options={donutOptions} series={[plan.count, credits.count]} type="donut" height={260} />
          ) : (
            <EmptyState text="No onboarded users in this period" />
          )}
        </Paper>

        <Paper sx={cardSx}>
          <SectionHeader
            icon={<WorkspacePremiumOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
            title="Subscription Plan Path"
            subtitle="Standard / Premium subscribers"
          />
          {plan.count > 0 ? (
            <Box sx={{ display: "flex", justifyContent: "space-around", py: 2 }}>
              <MiniStat value={plan.count} label="Plan Subscribers" color={THEME_COLOR} />
              <MiniStat
                value={plan.paid}
                label="Purchased a Plan"
                sublabel={`${formatPct(planPaidRate)} of plan subscribers`}
                color="#22c55e"
              />
            </Box>
          ) : (
            <EmptyState text="No plan subscribers in this period" />
          )}
        </Paper>
      </Box>

      {/* Credit Usage & Paywall Engagement (all onboarded users) */}
      <Paper sx={{ ...cardSx, mb: 3 }}>
        <SectionHeader
          icon={<LocalFireDepartmentOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
          title="Credit Usage & Paywall Engagement"
          subtitle="Of all onboarded users, regardless of plan — a leading indicator of who would hit the paywall"
        />
        {creditEngagement.onboarded > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 2,
              py: 2,
            }}
          >
            <MiniStat value={creditEngagement.onboarded} label="Completed Onboarding" color={THEME_COLOR} />
            <MiniStat
              value={creditEngagement.usedCredits}
              label="Tried Credits"
              sublabel={`${formatPct(creditEngagement.usedCreditsRate)} of onboarded`}
              color="#f59e0b"
            />
            <MiniStat
              value={creditEngagement.ranOut}
              label="Depleted Credits"
              sublabel={`${formatPct(creditEngagement.ranOutRate)} of those who tried credits — hit the paywall`}
              color="#c01e2e"
            />
            <MiniStat
              value={creditEngagement.ranOutAndPaid}
              label="Then Purchased"
              sublabel={`${formatPct(creditEngagement.ranOutAndPaidRate)} of those who hit the paywall`}
              color="#22c55e"
            />
          </Box>
        ) : (
          <EmptyState text="No onboarded users in this period" />
        )}
        <Box sx={{ mt: creditEngagement.onboarded > 0 ? 1 : 0, pt: creditEngagement.onboarded > 0 ? 2 : 0, borderTop: creditEngagement.onboarded > 0 ? "1px solid rgba(26,26,26,0.07)" : "none" }}>
          <MiniStat
            value={creditUsageBeforePurchase.count > 0 ? creditUsageBeforePurchase.avgCreditsUsed : "—"}
            label="Avg Credits Used Before First Purchase (Plan or Top-up)"
            sublabel={
              creditUsageBeforePurchase.count > 0
                ? `Median ${creditUsageBeforePurchase.medianCreditsUsed} · based on ${creditUsageBeforePurchase.count} purchase${creditUsageBeforePurchase.count === 1 ? "" : "s"}`
                : "Tracking started — data appears after the next purchase"
            }
            color="#8b5cf6"
          />
        </Box>
      </Paper>

      {/* Pay-as-you-go Credits Path */}
      <Paper sx={{ ...cardSx, mb: 3 }}>
        <SectionHeader
          icon={<BoltOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
          title="Pay-as-you-go Credits Path"
          subtitle="Credit depletion → top-up conversion"
        />
        {credits.count > 0 ? (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
                py: 2,
                mb: 1,
              }}
            >
              <MiniStat value={credits.count} label="Credits Path" color={THEME_COLOR} />
              <MiniStat
                value={credits.depleted}
                label="Maxed Out Credits"
                sublabel={`${formatPct(creditsDepletedRate)} of credits path`}
                color="#f59e0b"
              />
              <MiniStat
                value={credits.paid}
                label="Purchased Top-up"
                sublabel={`${formatPct(creditsPaidRate)} of credits path`}
                color="#22c55e"
              />
            </Box>
            <HorizontalBarChart stages={creditsFunnelStages} />
            <DropoffRow stages={creditsFunnelStages} dropoffs={creditsFunnelDropoffs} />
          </>
        ) : (
          <EmptyState text="No pay-as-you-go users in this period" />
        )}
      </Paper>

      {/* Payment Outcomes */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1.4fr" }, gap: 3 }}>
        <Paper sx={cardSx}>
          <SectionHeader
            icon={<PaidOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
            title="Payment Outcomes"
            subtitle="Of all signups in this period, regardless of onboarding status"
          />
          {conversionFunnel.totalSignups > 0 ? (
            <HorizontalBarChart stages={paymentOutcomeStages} colors={["#94a3b8", "#22c55e"]} percentOf="sum" />
          ) : (
            <EmptyState text="No signups in this period" />
          )}
        </Paper>

        <Paper sx={cardSx}>
          <SectionHeader
            icon={<ReceiptLongOutlinedIcon sx={{ color: THEME_COLOR, fontSize: 18 }} />}
            title="Initial Payment Amount"
            subtitle="First payment made by paying users"
          />
          {totalBucketCount > 0 ? (
            <>
              <Chart options={bucketChartOptions} series={[{ name: "Users", data: paymentBreakdown.buckets.map((b) => b.count) }]} type="bar" height={220} />
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Payment Type</TableCell>
                    <TableCell sx={headCellSx} align="right">Users</TableCell>
                    <TableCell sx={headCellSx} align="right">Revenue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentBreakdown.byType.map((t) => (
                    <TableRow key={t.type}>
                      <TableCell sx={cellSx}>{formatRevenueType(t.type)}</TableCell>
                      <TableCell sx={cellSx} align="right">{t.count}</TableCell>
                      <TableCell sx={cellSx} align="right">{formatGBP(t.totalRevenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <EmptyState text="No payments from users in this period" />
          )}
        </Paper>
      </Box>

      <MarketingAssistantChat
        context={
          isCustomRange
            ? { startDate: customRange.startDate, endDate: customRange.endDate, channel }
            : { days, channel }
        }
      />
    </Box>
  );
}
